import type { EmailRepository } from '../application/EmailRepository'
import type {
  EmailInboxFilters,
  EmailInboxPage,
  EmailComposeOption,
  EmailDraft,
  EmailMailbox,
  EmailReassignCommand,
  EmailStatusCommand,
  EmailThreadCommand,
  EmailThreadCommandResult,
  EmailThreadDetail,
  EmailThreadViewer,
  SaveEmailDraftCommand,
  SaveEmailDraftResult,
  SendEmailResult,
  WaiveEmailSlaCommand,
} from '../domain/email'

interface RpcResponse {
  data: unknown
  error: { message: string } | null
}
interface EmailSupabaseClient {
  rpc(name: string, args?: Record<string, unknown>): Promise<RpcResponse>
  storage: {
    from(bucket: string): {
      createSignedUrl(
        path: string,
        expiresIn: number,
      ): Promise<{ data: { signedUrl: string } | null; error: { message: string } | null }>
      upload(
        path: string,
        body: File,
        options: { contentType: string; upsert: boolean },
      ): Promise<{ error: { message: string } | null }>
      remove(paths: string[]): Promise<{ error: { message: string } | null }>
    }
  }
  functions: {
    invoke(
      name: string,
      options: { body: unknown },
    ): Promise<{ data: unknown; error: { message: string } | null }>
  }
}

function unwrap(response: RpcResponse): unknown {
  if (response.error) throw new Error(response.error.message)
  return response.data
}

export function createSupabaseEmailRepository(client: EmailSupabaseClient): EmailRepository {
  async function command(
    name: string,
    value: EmailThreadCommand | EmailReassignCommand | EmailStatusCommand,
  ) {
    return unwrap(await client.rpc(name, { p_command: value })) as EmailThreadCommandResult
  }

  return {
    async listMailboxes() {
      return unwrap(await client.rpc('email_mailboxes_v1')) as EmailMailbox[]
    },
    async listInbox(filters: EmailInboxFilters) {
      const projection = filters.view === 'SENT' ? 'email_sent_v1' : 'email_inbox_v1'
      const projectedFilters = { ...filters }
      if (filters.view === 'SENT') delete projectedFilters.view
      return unwrap(await client.rpc(projection, { p_filters: projectedFilters })) as EmailInboxPage
    },
    async getThread(threadId: string) {
      return unwrap(
        await client.rpc('email_thread_detail_v1', { p_thread_id: threadId }),
      ) as EmailThreadDetail
    },
    async getComposeOptions() {
      return unwrap(await client.rpc('email_compose_options_v1')) as EmailComposeOption[]
    },
    async listDrafts() {
      return unwrap(await client.rpc('email_drafts_v1')) as EmailDraft[]
    },
    async saveDraft(value: SaveEmailDraftCommand) {
      return unwrap(await client.rpc('save_email_draft_v1', { p_command: value })) as SaveEmailDraftResult
    },
    async deleteDraft(draftId: string, attachmentPaths = []) {
      if (attachmentPaths.length > 0) {
        const removed = await client.storage.from('email-attachments').remove(attachmentPaths)
        if (removed.error) throw new Error(removed.error.message)
      }
      unwrap(await client.rpc('delete_email_draft_v1', { p_draft_id: draftId }))
    },
    async addDraftAttachment(draftId: string, file: File) {
      const mimeType = file.type || 'application/octet-stream'
      const metadata = unwrap(
        await client.rpc('add_email_draft_attachment_v1', {
          p_command: { draftId, filename: file.name, mimeType, sizeBytes: file.size },
        }),
      ) as { id: string; storagePath: string }
      const uploaded = await client.storage.from('email-attachments').upload(metadata.storagePath, file, {
        contentType: mimeType,
        upsert: false,
      })
      if (uploaded.error) {
        // La réservation SQL sans fichier ferait échouer l'envoi : on la retire avant de signaler l'erreur.
        await client.rpc('remove_email_draft_attachment_v1', { p_attachment_id: metadata.id })
        throw new Error(uploaded.error.message)
      }
      return {
        id: metadata.id,
        filename: file.name,
        mimeType,
        sizeBytes: file.size,
        storagePath: metadata.storagePath,
      }
    },
    async removeDraftAttachment(attachmentId: string, storagePath: string) {
      // Le fichier d'abord (la règle Storage exige que la ligne existe encore), puis la ligne.
      if (storagePath) {
        const removed = await client.storage.from('email-attachments').remove([storagePath])
        if (removed.error) throw new Error(removed.error.message)
      }
      unwrap(await client.rpc('remove_email_draft_attachment_v1', { p_attachment_id: attachmentId }))
    },
    async setDraftSettle(draftId: string, settle: boolean) {
      unwrap(await client.rpc('set_email_draft_settle_v1', { p_draft_id: draftId, p_settle: settle }))
    },
    async sendDraft(draftId: string, clientRequestId: string) {
      const result = await client.functions.invoke('email-send', { body: { draftId, clientRequestId } })
      if (result.error) {
        const context = (result.error as { context?: unknown }).context
        const body: unknown = context instanceof Response ? await context.json().catch(() => null) : null
        const code =
          body && typeof body === 'object' && 'error' in body && typeof body.error === 'string'
            ? body.error
            : result.error.message
        throw new Error(code)
      }
      return result.data as SendEmailResult
    },
    async markRead(threadId: string) {
      unwrap(await client.rpc('mark_email_thread_read_v1', { p_thread_id: threadId }))
    },
    async listThreadViewers(threadId: string) {
      return unwrap(
        await client.rpc('email_thread_viewers_v1', { p_thread_id: threadId }),
      ) as EmailThreadViewer[]
    },
    claim: (value) => command('claim_email_thread_v1', value),
    reassign: (value) => command('reassign_email_thread_v1', value),
    unassign: (value) => command('unassign_email_thread_v1', value),
    changeStatus: (value) => command('change_email_thread_status_v1', value),
    async waiveSla(value: WaiveEmailSlaCommand) {
      unwrap(await client.rpc('waive_email_sla_v1', { p_command: value }))
    },
    async createAttachmentUrl(attachmentId: string) {
      const attachment = unwrap(
        await client.rpc('open_email_attachment_v1', { p_attachment_id: attachmentId }),
      ) as { storagePath: string }
      const response = await client.storage
        .from('email-attachments')
        .createSignedUrl(attachment.storagePath, 300)
      if (response.error || !response.data)
        throw new Error(response.error?.message ?? 'EMAIL_ATTACHMENT_URL_MISSING')
      return response.data.signedUrl
    },
  }
}
